// Shared circuit-breaker for "reload to recover from a stale build" paths.
//
// Two callers use this with different sessionStorage keys so a chunk-preload
// reload doesn't suppress a later app-crash reload (and vice versa) — they're
// independent failure modes:
//   - stale-chunk-handler.ts → key "stale_chunk_reload" (vite:preloadError)
//   - AppErrorBoundary.tsx   → key "app_crash_reload"   (render-time crash)
//
// We allow ONE auto-reload per (key, buildId). If the same path triggers again
// on the same build, we don't loop — control returns to the caller, which is
// expected to surface a manual-reload UI.

import * as Sentry from "@sentry/react";

declare const __APP_VERSION__: string;
declare const __COMMIT_SHA__: string;

const appVersion =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "unknown";
const commitSha =
  typeof __COMMIT_SHA__ !== "undefined" ? __COMMIT_SHA__ : "unknown";

// Commit SHA, not version alone — same-version redeploys still produce new
// chunk hashes, and the circuit breaker has to track actual code changes.
const buildId = `${appVersion}-${commitSha}`;

interface ReloadOptions {
  /** sessionStorage key — caller scopes the breaker to its own failure mode. */
  key: string;
  /** Sentry tag — short identifier for the trigger (e.g. "stale_chunk", "app_crash"). */
  reason: string;
  /** Optional structured context attached to the Sentry event. */
  context?: Record<string, unknown>;
  /** Error captured by the caller, if any — included in the Sentry event. */
  error?: unknown;
}

/**
 * Attempts a one-shot reload to recover from a suspected stale-build state.
 * Returns true if a reload was scheduled, false if the breaker had already
 * tripped for this build (caller should fall back to a manual-reload UI).
 */
export function reloadForStaleBuild(opts: ReloadOptions): boolean {
  const hasReloaded = sessionStorage.getItem(opts.key) === buildId;

  Sentry.withScope((scope) => {
    scope.setTag("error_type", opts.reason);
    scope.setTag("app_version", appVersion);
    scope.setTag("already_reloaded", String(hasReloaded));
    scope.setLevel("warning");
    scope.setContext("reload_for_stale_build", {
      buildId,
      key: opts.key,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      ...opts.context,
    });

    if (opts.error instanceof Error) {
      Sentry.captureException(opts.error);
    } else {
      Sentry.captureException(
        new Error(`Stale-build reload (${opts.reason}, v${appVersion})`)
      );
    }
  });

  if (hasReloaded) return false;

  sessionStorage.setItem(opts.key, buildId);
  Sentry.flush(2000).finally(() => {
    window.location.reload();
  });
  return true;
}

export const currentBuildId = buildId;
